import { useEffect } from 'react';

/**
 * Hold a screen wake lock for as long as the page is visible.
 *
 * A stopwatch you are looking at must not have the screen dim and lock out from
 * under it. Both of the states this app is in while open outlast a typical
 * auto-lock: a run counting up, and — just as much — 00:00.0 held ready while
 * you wait for the thing you are about to time. Waiting for the gun is a
 * stopwatch's most ordinary posture, which is why this keys off the page being
 * visible rather than off the run, matching the iOS app's `isIdleTimerDisabled`
 * rule exactly.
 *
 * Two things about the API are easy to get wrong:
 *
 * - **The browser releases the lock whenever the page is hidden**, and never
 *   re-takes it. Without the `visibilitychange` handler below the lock works
 *   once and is silently gone after the first tab switch or incoming call —
 *   which looks exactly like the feature working, until it doesn't.
 * - **`request` rejects** rather than resolving to null when the user agent
 *   declines: the document is not visible, or the device is low on battery.
 *   That is the browser's call to make, so it is caught and dropped.
 */
export default function useWakeLock() {
  useEffect(() => {
    // Safari 16.4+ / Chrome 84+, and secure-context only: over plain http
    // (localhost aside) `navigator.wakeLock` is simply absent. Nothing here is
    // load-bearing, so an unsupported browser just keeps its normal behaviour.
    if (!('wakeLock' in navigator)) return undefined;

    // The lock we currently hold, if any. Never assume it survives — see above.
    let sentinel = null;
    // Set by the cleanup so a request still in flight when the effect is torn
    // down releases what it gets instead of leaking it. React 18 StrictMode
    // mounts, unmounts and remounts every effect in development, so this is the
    // ordinary path rather than an edge case.
    let stale = false;

    const acquire = async () => {
      if (stale || sentinel || document.visibilityState !== 'visible') return;
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (stale) {
          lock.release().catch(() => {});
          return;
        }
        sentinel = lock;
        // The only channel the browser has to tell us it dropped the lock on
        // its own. Clearing the reference is what lets the handler below know
        // there is something to ask for again.
        lock.addEventListener('release', () => {
          if (sentinel === lock) sentinel = null;
        });
      } catch {
        // The user agent declined. Not an error the app can or should act on.
      }
    };

    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') acquire();
    };

    acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      stale = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (sentinel) sentinel.release().catch(() => {});
      sentinel = null;
    };
  }, []);
}
